import { IdentityStorageDto } from "./identity-storage-dto";
import { inject, singleton, injectWithTransform } from "tsyringe";
import {
  FandomContextGroups,
  ContextRepository,
  Logger,
  LoggerTransformer,
} from "@identity/utils";
import { InstantConfigService } from "../../InstantConfig";
import { IdentityStorageClient } from "./identity-storage-client";
import { SENTRY } from "@identity/symbols";
import { SentryLoader } from "../../../sentry-loader";

@singleton()
export class IdentityStorageService {
  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "UserIdentity")
    private logger: Logger,
    @inject(ContextRepository)
    private windowContextRepository: ContextRepository,
    @inject(InstantConfigService)
    private instantConfigService: InstantConfigService,
    @inject(IdentityStorageClient)
    private identityStorageClient: IdentityStorageClient,
    @inject(SENTRY) private readonly sentry: SentryLoader,
  ) {}

  async get(): Promise<IdentityStorageDto> {
    const localData = await this.getLocalData();
    if (localData && !localData.synced) {
      this.setRemote(localData);
    }
    if (!localData || this.isDeprecated(localData)) {
      return this.getRemote();
    } else {
      this.setIdentityContextVariables(localData);
      return localData;
    }
  }

  async setRemote(data: IdentityStorageDto) {
    try {
      const remoteData = await this.identityStorageClient.postData(data);
      const localData = this.identityStorageClient.getLocalData();
      this.setIdentityContextVariables(localData);
      this.identityStorageClient.setLocalData({
        ...localData,
        timestamp: Date.now(),
        synced: remoteData.synced,
      });
    } catch (ex) {
      if (ex instanceof Error) {
        this.sentry.captureException(ex);
      }

      this.logger.error("Updating Identity LocalStorage data failed");
    }
  }

  private isDeprecated(localData: IdentityStorageDto): boolean {
    // Fallback : days * hours * minutes * seconds * milliseconds;
    const timeDifference = this.instantConfigService.get(
      "icIdentityTtl",
      7 * 24 * 60 * 60 * 1000,
    );
    const lastWeek = Date.now() - timeDifference;
    return !localData.timestamp || localData.timestamp - lastWeek < 0;
  }

  private async getLocalData() {
    return this.identityStorageClient.getLocalData();
  }

  private async getRemote(): Promise<IdentityStorageDto> {
    const remoteData = await this.identityStorageClient.fetchData();
    this.setIdentityContextVariables(remoteData);
    this.identityStorageClient.setLocalData({
      ...remoteData,
      timestamp: Date.now(),
    });
    return remoteData;
  }

  private setIdentityContextVariables(userData: IdentityStorageDto) {
    if (userData.over18) {
      this.windowContextRepository.set(
        FandomContextGroups.tracking,
        "over_18",
        "1",
      );
    }
  }
}
